import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { getEvent, updateEvent } from '../lib/firestore/events';
import { Timestamp } from 'firebase/firestore';
import { isEventCohost } from '../lib/firestore/eventCohosts';
import { getGroupMember } from '../lib/firestore/groups';
import { isAdmin } from '../lib/firestore/admins';
import { storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import QRCodeModal from '../components/QRCodeModal';
import '../styles/EditEvent.css';

const EditEvent = () => {
  const { eventId } = useParams();
  const { user, loading: authLoading } = useAuthState();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);
  const [eventImageUrl, setEventImageUrl] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    endDate: '',
    endTime: '',
    location: '',
    capacity: '',
    visibility: 'invite_only',
    website: '',
    bringAGuest: 0,
    membersCanInvite: false,
    maxSponsorsPerMember: undefined, // 0 or blank = unlimited (like groups)
    attendeesVisible: 'off',
    sponsorshipAtDoor: false,
    enablePreEventBoard: false,
    enableDuringEventBoard: false,
    enableDecompressionBoard: false,
    decompressionDuration: 7,
    enableGroupChat: false,
  });

  useEffect(() => {
    console.log('🔵 [EditEvent] useEffect triggered:', { eventId, hasUser: !!user, userId: user?.uid, authLoading });
    
    if (!eventId) {
      console.error('🔴 [EditEvent] Missing eventId - this should not happen if route is correct');
      // Don't navigate to home - let the route handle it
      return;
    }
    
    // Wait for auth to finish loading before checking user
    if (authLoading) {
      console.log('🔵 [EditEvent] Auth still loading, waiting...');
      return;
    }
    
    // After auth finishes loading, check if user exists
    if (!user) {
      console.log('🔵 [EditEvent] No user after auth loaded, redirecting to login');
      navigate('/login');
      return;
    }
    
    // User exists and auth is loaded, load the event
    if (eventId && user) {
      console.log('🟢 [EditEvent] Conditions met, loading event');
      loadEvent();
    }
  }, [eventId, user, authLoading, navigate]);

  const loadEvent = async () => {
    if (!eventId || !user) {
      console.warn('🔴 [EditEvent] Cannot load event - missing eventId or user');
      return;
    }

    setLoading(true);
    setError('');
    try {
      console.log('🔵 [EditEvent] Loading event:', eventId);
      const eventData = await getEvent(eventId);
      if (!eventData) {
        console.error('🔴 [EditEvent] Event not found:', eventId);
        setError('Event not found');
        setLoading(false);
        return;
      }
      console.log('🟢 [EditEvent] Event loaded:', eventData);

      // Check if user can edit:
      // 1. User is the direct host (profile events) - also check if hostId matches even if hostType is missing
      // 2. User created the event (createdBy field)
      // 3. User is an event cohost
      // 4. If event is hosted by a group: user is group owner/cohost
      const userIsHost = (eventData.hostId === user.uid && eventData.hostType === 'profile') || 
                         (eventData.hostId === user.uid && !eventData.hostType); // Fallback for old events
      const userIsCreator = eventData.createdBy === user.uid;
      const userIsCohost = await isEventCohost(eventId, user.uid);
      
      let userIsGroupOwnerOrCohost = false;
      if (eventData.hostType === 'group') {
        try {
          console.log('🔵 [EditEvent] Checking group membership for group:', eventData.hostId, 'user:', user.uid);
          const member = await getGroupMember(eventData.hostId, user.uid);
          console.log('🔵 [EditEvent] Group member data:', member);
          userIsGroupOwnerOrCohost = member?.role === 'owner' || member?.role === 'cohost';
          console.log('🔵 [EditEvent] userIsGroupOwnerOrCohost:', userIsGroupOwnerOrCohost);
        } catch (err) {
          console.error('🔴 [EditEvent] Error checking group membership:', err);
          userIsGroupOwnerOrCohost = false;
        }
      }
      
      const userIsAdmin = await isAdmin(user.uid);
      const canEdit = userIsHost || userIsCreator || userIsCohost || userIsGroupOwnerOrCohost || userIsAdmin;
      console.log('🔵 [EditEvent] Permission check:', {
        eventId,
        userId: user.uid,
        hostId: eventData.hostId,
        hostType: eventData.hostType,
        createdBy: eventData.createdBy,
        userIsHost,
        userIsCreator,
        userIsCohost,
        userIsGroupOwnerOrCohost,
        userIsAdmin,
        canEdit
      });
      
      if (!canEdit) {
        setError('You can only edit events you created or are a cohost of');
        setLoading(false);
        // Don't navigate immediately - let user see the error
        setTimeout(() => {
          navigate(`/events/${eventId}`);
        }, 2000);
        return;
      }

      setEvent(eventData);

      // Populate form
      const startDate = new Date(eventData.startsAt);
      const endDate = eventData.endsAt ? new Date(eventData.endsAt) : null;

      setFormData({
        title: eventData.title,
        description: eventData.description || '',
        date: startDate.toISOString().split('T')[0],
        time: startDate.toTimeString().slice(0, 5),
        endDate: endDate ? endDate.toISOString().split('T')[0] : '',
        endTime: endDate ? endDate.toTimeString().slice(0, 5) : '',
        location: eventData.location?.text || '',
        capacity: eventData.capacity?.toString() || '',
        visibility: eventData.visibility || 'invite_only',
        website: eventData.website || '',
        bringAGuest: eventData.rules?.bringAGuest ?? 0,
        membersCanInvite: eventData.rules?.membersCanInvite === true || (eventData.rules?.maxSponsorsPerMember !== undefined && eventData.rules?.maxSponsorsPerMember !== null),
        maxSponsorsPerMember: eventData.rules?.maxSponsorsPerMember !== undefined && eventData.rules?.maxSponsorsPerMember !== null ? eventData.rules.maxSponsorsPerMember : undefined,
        attendeesVisible: eventData.rules?.attendeesVisible || 'off',
        sponsorshipAtDoor: eventData.rules?.sponsorshipAtDoor || false,
        enablePreEventBoard: eventData.messageBoards?.preEvent || false,
        enableDuringEventBoard: eventData.messageBoards?.duringEvent || false,
        enableDecompressionBoard: eventData.messageBoards?.decompression?.enabled || false,
        decompressionDuration: eventData.messageBoards?.decompression?.durationDays || 7,
        enableGroupChat: eventData.groupChat?.enabled || false,
      });
      setEventImageUrl(eventData.image || null);
      console.log('🟢 [EditEvent] Form data populated');
    } catch (err) {
      console.error('🔴 [EditEvent] Error loading event:', err);
      setError(err.message || 'Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!user) {
      alert('Please log in to upload images');
      return;
    }

    setUploadingImage(true);
    try {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        setUploadingImage(false);
        return;
      }

      // Upload to Firebase Storage
      const fileRef = ref(storage, `event-images/${user.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      setEventImageUrl(downloadURL);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!event || !user) return;

    setSaving(true);
    setError('');

    try {
      const startsAt = new Date(`${formData.date}T${formData.time}`);
      // Only set endsAt if both endDate and endTime are provided
      const endsAt =
        formData.endDate && formData.endTime
          ? new Date(`${formData.endDate}T${formData.endTime}`)
          : null; // Use null instead of undefined

      // Build updates object, only including defined values
      const updates = {
        title: formData.title,
        startsAt,
        location: {
          text: formData.location || 'TBD',
        },
        visibility: formData.visibility,
      };

      // Include image if it was updated
      if (eventImageUrl && eventImageUrl.trim()) {
        updates.image = eventImageUrl.trim();
      } else if (eventImageUrl === null && event.image) {
        // If image was removed (set to null), include null to clear it
        updates.image = null;
      }

      // Only add endsAt if it's not null
      if (endsAt) {
        updates.endsAt = endsAt;
      }

      // Only add optional fields if they have values
      if (formData.description && formData.description.trim()) {
        updates.description = formData.description.trim();
      }
      if (formData.capacity) {
        updates.capacity = parseInt(formData.capacity);
      }
      if (formData.website && formData.website.trim()) {
        updates.website = formData.website.trim();
      }

      // Add rules
      updates.rules = {
        attendeesVisible: formData.attendeesVisible,
        membersCanInvite: formData.membersCanInvite,
        bringAGuest: formData.bringAGuest,
        sponsorshipAtDoor: formData.sponsorshipAtDoor,
      };
      if (formData.membersCanInvite) {
        updates.rules.maxSponsorsPerMember = formData.maxSponsorsPerMember ?? 0; // 0 = unlimited
      }

      // Add message boards
      updates.messageBoards = {
        preEvent: formData.enablePreEventBoard,
        duringEvent: formData.enableDuringEventBoard,
        decompression: formData.enableDecompressionBoard ? {
          enabled: true,
          durationDays: formData.decompressionDuration,
          opened: event.messageBoards?.decompression?.opened || false,
          ...(event.messageBoards?.decompression?.openedAt && {
            openedAt: event.messageBoards.decompression.openedAt
          }),
        } : { enabled: false },
      };

      // Add group chat
      updates.groupChat = {
        enabled: formData.enableGroupChat,
      };

      // Backfill createdBy if missing (e.g. events created before field existed, or under group without it)
      if (!event.createdBy && user?.uid) {
        updates.createdBy = user.uid;
      }

      console.log('🔵 [EditEvent] Updating event with:', updates);
      console.log('🔵 [EditEvent] Event data before update:', {
        eventId,
        hostId: event?.hostId,
        hostType: event?.hostType,
        createdBy: event?.createdBy,
        userId: user.uid
      });
      await updateEvent(eventId, updates);
      console.log('🟢 [EditEvent] Update successful, navigating...');

      navigate(`/events/${eventId}`);
    } catch (err) {
      console.error('🔴 [EditEvent] Error updating event:', err);
      console.error('🔴 [EditEvent] Error code:', err?.code);
      console.error('🔴 [EditEvent] Error message:', err?.message);
      const errorMessage = err?.message || 'Failed to update event';
      // If it's a permission error, provide more helpful message
      if (errorMessage.includes('permission') || errorMessage.includes('Permission') || err?.code === 'permission-denied') {
        setError('Permission denied. You may not have permission to edit this event. Please check that you are the creator, host, cohost, or group owner/cohost.');
      } else {
        setError(errorMessage);
      }
      setSaving(false);
    }
  };

  // Show loading while auth is loading or event is loading
  if (authLoading || loading) {
    return (
      <div className="edit-event-container">
        <div className="loading">
          {authLoading ? 'Loading authentication...' : 'Loading event...'}
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="edit-event-container">
        <div className="error-message">{error || 'Event not found'}</div>
        <button className="btn btn-primary" onClick={() => navigate('/home')}>
          Go Home
        </button>
      </div>
    );
  }

  return (
    <div className="edit-event-container">
      <div className="edit-event-card">
        <header className="edit-header">
          <button className="btn-back" onClick={() => navigate(`/events/${eventId}`)}>
            ← Back to Event
          </button>
          <h1>Edit Event</h1>
          <p>{event.title}</p>
        </header>

        <form onSubmit={handleSubmit} className="edit-event-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-section">
            <h2>Basic Information</h2>

            {/* Event Image Upload */}
            <div className="form-group">
              <label htmlFor="eventImage">Event Photo</label>
              <div className="image-upload-section">
                <label htmlFor="eventImage" className="image-upload-label">
                  {eventImageUrl ? (
                    <img src={eventImageUrl} alt="Event" className="preview-image" />
                  ) : (
                    <div className="image-upload-placeholder">
                      <span>📷</span>
                      <small>Add Photo</small>
                    </div>
                  )}
                </label>
                <input
                  type="file"
                  id="eventImage"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  disabled={uploadingImage}
                />
                {uploadingImage && <div className="uploading-indicator">Uploading...</div>}
                {eventImageUrl && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setEventImageUrl(null)}
                    style={{ marginTop: '0.5rem' }}
                  >
                    Remove Photo
                  </button>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="title">Event Title *</label>
              <input
                type="text"
                id="title"
                className="input"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                required
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                className="input"
                rows={4}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                maxLength={1000}
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Date & Time</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date">Start Date *</label>
                <input
                  type="date"
                  id="date"
                  className="input"
                  value={formData.date}
                  onChange={(e) =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="time">Start Time *</label>
                <input
                  type="time"
                  id="time"
                  className="input"
                  value={formData.time}
                  onChange={(e) =>
                    setFormData({ ...formData, time: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="endDate">End Date (Optional)</label>
                <input
                  type="date"
                  id="endDate"
                  className="input"
                  value={formData.endDate}
                  onChange={(e) =>
                    setFormData({ ...formData, endDate: e.target.value })
                  }
                />
              </div>

              <div className="form-group">
                <label htmlFor="endTime">End Time (Optional)</label>
                <input
                  type="time"
                  id="endTime"
                  className="input"
                  value={formData.endTime}
                  onChange={(e) =>
                    setFormData({ ...formData, endTime: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Location & Capacity</h2>

            <div className="form-group">
              <label htmlFor="location">Location</label>
              <input
                type="text"
                id="location"
                className="input"
                value={formData.location}
                onChange={(e) =>
                  setFormData({ ...formData, location: e.target.value })
                }
                placeholder="e.g., Central Park, John's House"
              />
            </div>

            <div className="form-group">
              <label htmlFor="website">Website (Optional)</label>
              <input
                type="url"
                id="website"
                className="input"
                value={formData.website}
                onChange={(e) =>
                  setFormData({ ...formData, website: e.target.value })
                }
                placeholder="https://example.com"
              />
              <small className="form-hint">
                Link to event website, registration page, or more info
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="capacity">Capacity (Optional)</label>
              <input
                type="number"
                id="capacity"
                className="input"
                min="1"
                value={formData.capacity}
                onChange={(e) =>
                  setFormData({ ...formData, capacity: e.target.value })
                }
                placeholder="Max number of attendees"
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Visibility & Access</h2>

            <div className="form-group">
              <label htmlFor="visibility">Visibility *</label>
              <select
                id="visibility"
                className="input"
                value={formData.visibility}
                onChange={(e) =>
                  setFormData({ ...formData, visibility: e.target.value })
                }
                required
              >
                <option value="invite_only">Invite Only</option>
                <option value="invite_or_apply">Invite or Apply to Join</option>
                <option value="public">Public (Listed)</option>
                {event?.hostType === 'group' && (
                  <option value="group_only">Visible to Group Members</option>
                )}
                {event?.hostType === 'event' && (
                  <option value="event_only">Visible to Event Attendees</option>
                )}
              </select>
              <small className="form-hint">
                {formData.visibility === 'invite_only' && 'Only people with invite links can see and join'}
                {formData.visibility === 'invite_or_apply' && 'People can request to join or be invited'}
                {formData.visibility === 'public' && 'Anyone can discover and join'}
                {formData.visibility === 'group_only' && 'Visible to all members of the parent group. Appears in discover for group members only.'}
                {formData.visibility === 'event_only' && 'Visible to all attendees of the parent event. Appears in discover for event attendees only.'}
              </small>
            </div>
          </div>

          <div className="form-section">
            <h2>Event Rules</h2>

            <div className="form-group">
              <label htmlFor="bringAGuest">How many guests can each attendee bring?</label>
              <input
                type="number"
                id="bringAGuest"
                className="input"
                min="0"
                max="10"
                value={formData.bringAGuest}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    bringAGuest: parseInt(e.target.value) || 0,
                  })
                }
              />
              <small className="form-hint">
                How many guests can each attendee bring with them to the event? Zero means unlimited.
              </small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.membersCanInvite}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      membersCanInvite: e.target.checked,
                    })
                  }
                />
                Members Can Invite Others
              </label>
              <small className="form-hint">
                Allow attendees to invite/sponsor additional people to this event (like group sponsorship).
              </small>
            </div>

            {formData.membersCanInvite && (
              <div className="form-group">
                <label htmlFor="maxSponsorsPerMember">Max invites per attendee</label>
                <input
                  type="number"
                  id="maxSponsorsPerMember"
                  className="input"
                  min="0"
                  max="10"
                  placeholder="0 = unlimited"
                  value={formData.maxSponsorsPerMember === undefined || formData.maxSponsorsPerMember === null ? '' : formData.maxSponsorsPerMember}
                  onChange={(e) => {
                    const value = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                    const clamped = value !== undefined && value > 10 ? 10 : value;
                    setFormData({ ...formData, maxSponsorsPerMember: clamped });
                  }}
                />
                <small className="form-hint">
                  Limit how many people each attendee can invite. Zero or leave blank = unlimited.
                </small>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="attendeesVisible">Attendees List Visibility</label>
              <select
                id="attendeesVisible"
                className="input"
                value={formData.attendeesVisible}
                onChange={(e) =>
                  setFormData({ ...formData, attendeesVisible: e.target.value })
                }
              >
                <option value="off">Hidden (Default)</option>
                <option value="mutuals_only">Mutuals Only</option>
                <option value="friends_only">Friends Only</option>
              </select>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.sponsorshipAtDoor}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sponsorshipAtDoor: e.target.checked,
                    })
                  }
                />
                Allow Sponsorship at Door
              </label>
              <small className="form-hint">
                When enabled, attendees can invite/sponsor others on the spot at the event (e.g. at check-in or at the door).
                The setting is saved; full at-door invite flow may be expanded later.
              </small>
            </div>
          </div>

          <div className="form-section">
            <h2>Message Boards</h2>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.enablePreEventBoard}
                  onChange={(e) =>
                    setFormData({ ...formData, enablePreEventBoard: e.target.checked })
                  }
                />
                Enable Pre-Event Message Board
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.enableDuringEventBoard}
                  onChange={(e) =>
                    setFormData({ ...formData, enableDuringEventBoard: e.target.checked })
                  }
                />
                Enable During-Event Message Board
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.enableDecompressionBoard}
                  onChange={(e) =>
                    setFormData({ ...formData, enableDecompressionBoard: e.target.checked })
                  }
                />
                Enable Post-Event Decompression Board
              </label>
            </div>

            {formData.enableDecompressionBoard && (
              <div className="form-group">
                <label htmlFor="decompressionDuration">
                  Decompression Board Duration (days)
                </label>
                <input
                  type="number"
                  id="decompressionDuration"
                  className="input"
                  min="3"
                  max="14"
                  value={formData.decompressionDuration}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      decompressionDuration: parseInt(e.target.value) || 7,
                    })
                  }
                />
              </div>
            )}

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.enableGroupChat}
                  onChange={(e) =>
                    setFormData({ ...formData, enableGroupChat: e.target.checked })
                  }
                />
                Allow Group Chat
              </label>
              <small className="form-hint">
                Enable a group chat that opens 24 hours before the event and closes 24 hours after it ends
              </small>
            </div>
          </div>

          {/* Cohosts and QR Code Section */}
          {event && event.hostId === user?.uid && (
            <div className="form-section">
              <h2>Event Management</h2>
              
              <div className="form-group">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate(`/events/${eventId}/cohosts`)}
                >
                  Manage Cohosts
                </button>
                <small className="form-hint">
                  Add or remove cohosts who can help manage this event
                </small>
              </div>

              {event.visibility === 'public' && (
                <div className="form-group">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowQRModal(true)}
                  >
                    📱 Show QR Code for Invites
                  </button>
                  <small className="form-hint">
                    Generate a QR code that others can scan to join this event
                  </small>
                </div>
              )}

              <div className="form-group">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate(`/events/${eventId}/checkin`)}
                >
                  Open Check-In QR
                </button>
                <small className="form-hint">
                  Generate a QR code for checking in attendees at the event
                </small>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(`/events/${eventId}`)}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

          <div className="danger-zone">
            <h3>Danger Zone</h3>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => navigate(`/events/${eventId}/delete`)}
            >
              Delete Event
            </button>
            <p className="danger-hint">
              Permanently delete this event. This action cannot be undone.
            </p>
          </div>
        </form>

        {/* QR Code Modal */}
        {showQRModal && user && event && (
          <QRCodeModal
            isOpen={showQRModal}
            onClose={() => setShowQRModal(false)}
            eventId={eventId}
            issuedBy={user.uid}
          />
        )}
      </div>
    </div>
  );
};

export default EditEvent;

